import ProductListFilter from '@organisms/ProductListFilter/ProductListFilter';
import SelectOption from '@molecules/Select/Option';
import SelectBox from '@molecules/Select/SelectBox';
import { useEffect, useMemo, useState } from 'react';
import IconTooltip from '@molecules/IconTooltip/IconTooltip';
import Text from '@atoms/Text/Text';
import Link from '@atoms/Link/Link';
import paths from '@constants/paths';
import {
  FilterAndSortWrapper,
  SelectWrapper,
  StyledAboutThisSearch,
  StyledNumberOfResults,
} from './FilterAndSort.styles';
import type { BreadcrumbDataSearchStateData, FacetDataObject } from '@occ/api-client';
import SelectedFilters from '@molecules/SelectedFilters/SelectedFilters';
import useResponsive from '@hooks/useResponsive';
import Questionmark from '@icons/non-standard/QUESTIONMARK-16px.svg';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';

interface Props {
  totalResults?: number;
  filterOptions?: FacetDataObject[];
  selectedFilters?: Array<BreadcrumbDataSearchStateData>;
  sortOptions?: OptionType[] | null;
  onSort?: (option: OptionType) => void;
  gtmTrackOnFilterOpened?: boolean;
  forceHideRelevanceTooltip?: boolean;
  isLoading?: boolean;
}

const FilterAndSort = ({
  totalResults = 0,
  filterOptions,
  selectedFilters = [],
  sortOptions,
  onSort,
  gtmTrackOnFilterOpened = true,
  forceHideRelevanceTooltip = false,
  isLoading,
}: Props) => {
  const tProductFilter = useTranslations('productFilter');
  const tTooltip = useTranslations('tooltip');
  const tCommon = useTranslations('common');

  const [selectedOption, setSelectedOption] = useState({} as OptionType);
  const showRelevanceTooltip =
    !forceHideRelevanceTooltip && selectedOption?.value === 'relevance' && selectedOption?.selected && totalResults > 0;
  const { isMobile } = useResponsive();
  const pathname = usePathname();

  const isSearchResultPage = useMemo(() => {
    return pathname?.startsWith(paths.SEARCH);
  }, [pathname]);

  const isPromotionPage = useMemo(() => {
    return pathname?.startsWith(paths.OFFERS);
  }, [pathname]);

  useEffect(() => {
    if (sortOptions?.length && Object.keys(selectedOption).length === 0) setSelectedOption(sortOptions[0]);
  }, [sortOptions, selectedOption]);

  return (
    <>
      <FilterAndSortWrapper
        $noMargin={totalResults === 0}
        data-testid={sortOptions?.length && sortOptions.length > 0 ? 'filterandsort-loaded' : undefined}
      >
        <ProductListFilter
          totalNumberOfResults={totalResults}
          breadcrumbs={selectedFilters}
          facets={filterOptions}
          gtmTrackOnFilterOpened={gtmTrackOnFilterOpened}
          isLoading={isLoading}
        />

        {isPromotionPage && (
          <Text type="body" size="small">
            {tProductFilter('totalResults', { totalResult: totalResults || 0 })}
          </Text>
        )}

        {totalResults > 0 && sortOptions && sortOptions.length > 0 && (
          <SelectWrapper
            avoidViewport
            options={sortOptions}
            aria-label={tCommon('listbox.ariaLabelSort')}
            selectBoxComponent={() => <SelectBox type="subtitle" size="small" currentSelection={selectedOption} />}
            optionComponent={({ option, isSelected, onItemClickHandler }) => (
              <SelectOption
                key={option.value}
                option={option}
                isSelected={isSelected}
                onItemClick={() => {
                  if (onSort) {
                    setSelectedOption(option);
                    onSort(option);
                  }
                  onItemClickHandler(option);
                }}
              />
            )}
            selectedOptionIndex={sortOptions.findIndex((so) => so.selected)}
            testId="filterandsort-select"
          />
        )}
      </FilterAndSortWrapper>
      {selectedFilters?.length > 0 && !isMobile && <SelectedFilters breadcrumbs={selectedFilters} />}

      {(isSearchResultPage || showRelevanceTooltip) && (
        <StyledAboutThisSearch>
          {isSearchResultPage && isMobile && totalResults > 0 && (
            <StyledNumberOfResults type="body" size="small" color="black" data-testid="search-result-total-amount">
              {tProductFilter('totalResults', { totalResult: totalResults })}
            </StyledNumberOfResults>
          )}

          {showRelevanceTooltip && (
            <>
              <Text type="body" color="black" size="small">
                {tTooltip('tooltip.aboutThisSearchResult')}
              </Text>

              <IconTooltip
                openOnClick
                align="right"
                position="bottom"
                content={
                  <>
                    {tTooltip.rich('tooltip.aboutThisHits', {
                      link: (chunks) => <Link href={paths.ARTICLE.PERSONALIZATION}>{chunks}</Link>,
                    })}
                  </>
                }
                iconProps={{ svg: Questionmark, size: 16, 'aria-label': tCommon('aria.infoIcon') }}
                variant="medium"
              />
            </>
          )}
        </StyledAboutThisSearch>
      )}
    </>
  );
};

export default FilterAndSort;
